/**
 * LadyBoss Cart Drawer — cart-drawer.js
 * ─────────────────────────────────────────────────────────────
 * Performance strategy:
 *   - Section re-renders delegate to Symmetry's native CartForm.refresh()
 *     which uses theme.mergeNodes() — a smart diff that only updates
 *     DOM nodes that actually changed. Much faster than our old full-replace.
 *   - All display updates (subtotal, shipping bar, insurance price) run
 *     from a single fetchCart() result — no redundant fetches.
 *   - Qty +/− buttons debounced 300ms so rapid taps = 1 API call.
 *   - Optimistic UI runs synchronously before any API call.
 * ─────────────────────────────────────────────────────────────
 * Built: LadyBoss July 2026
 */

(function () {
  'use strict';

  // ─── CONFIG ──────────────────────────────────────────────────
  const INSURANCE_PRODUCT_HANDLE   = 'shipping-protection';
  const FREE_SHIPPING_THRESHOLD    = 9900;   // cents = $99.00
  // Products whose subscription triggers free shipping for the whole cart.
  // Add more handles here when additional products get free shipping enabled.
  const FREE_SHIPPING_SUB_HANDLES  = ['ignite-hormonal-support', 'ignite-hormonal-support-offer', 'vanilla-cake-lean-protein-20g-protein', 'creatine-hydration', 'ladyboss-creatine', 'ultra-glow-collagen', 'ladyboss-natural-fuel'];

  const INSURANCE_TIERS = [
    { min:     0, max:  2499, price:  50, variantId: '50081065763045' },
    { min:  2500, max:  3499, price: 100, variantId: '50081065795813' },
    { min:  3500, max:  4999, price: 150, variantId: '50081065828581' },
    { min:  5000, max:  6499, price: 250, variantId: '50081065861349' },
    { min:  6500, max:  8499, price: 300, variantId: '50081065894117' },
    { min:  8500, max: 10499, price: 400, variantId: '50081065926885' },
    { min: 10500, max: 12499, price: 450, variantId: '50081065959653' },
    { min: 12500, max: 14999, price: 550, variantId: '50081065992421' },
    { min: 15000, max: 17499, price: 650, variantId: '50081066025189' },
    { min: 17500, max: 19999, price: 750, variantId: '50081066057957' },
    { min: 20000, max: 24999, price: 900, variantId: '50081066090725' },
    { min: 25000, max: 29999, price: 1100, variantId: '50081066123493' },
    { min: 30000, max: 39999, price: 1400, variantId: '50081066156261' },
    { min: 40000, max: Infinity, price: 1800, variantId: '50081066189029' },
  ];

  // ─── STATE ───────────────────────────────────────────────────
  let isUpdating  = false;
  let qtyDebounce = {};
  let easyGiftRequestsInFlight = 0;
  let lastCartActivityAt = 0;
  let easyGiftEventsBound = false;
  let insuranceReconcileTimer = null;
  let insuranceReconcileRunning = false;
  let insuranceOptedOut = false;

  // ─── HELPERS ─────────────────────────────────────────────────

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  function getInsuranceTier(cartSubtotalCents) {
    return INSURANCE_TIERS.find(t => cartSubtotalCents >= t.min && cartSubtotalCents <= t.max)
      || INSURANCE_TIERS[INSURANCE_TIERS.length - 1];
  }

  /** Announce completed insurance changes without moving keyboard focus. */
  function announceInsuranceStatus(message) {
    const status = document.getElementById('lb-insurance-status');
    if (!status) return;
    status.textContent = '';
    window.setTimeout(() => { status.textContent = message; }, 30);
  }

  /** Analyze a cart object — returns subtotal ex-insurance, insurance item, and free-shipping flags */
  function analyzeCart(cart) {
    let subtotalExInsurance  = 0;
    let insuranceItem        = null;
    let hasRealItems         = false;
    // True when a qualifying subscription (e.g. IGNITE) is in the cart—
    // which grants free shipping for the entire order.
    let hasFreeShippingSub   = false;

    cart.items.forEach(item => {
      if (item.handle === INSURANCE_PRODUCT_HANDLE) {
        insuranceItem = item;
      } else {
        subtotalExInsurance += item.final_line_price;
        hasRealItems = true;
        if (item.selling_plan_allocation &&
            FREE_SHIPPING_SUB_HANDLES.includes(item.handle)) {
          hasFreeShippingSub = true;
        }
      }
    });

    return { subtotalExInsurance, insuranceItem, hasFreeShippingSub, hasRealItems };
  }

  // ─── SYMMETRY NATIVE REFRESH ─────────────────────────────────

  /**
   * Trigger Symmetry's CartForm.refresh() — this fetches the section HTML
   * and uses theme.mergeNodes() to do a smart diff update of only changed nodes.
   * Much faster than our old full innerHTML replace.
   * After the merge fires the 'on:cart:after-merge' event, we re-apply
   * our display updates so subtotal/bar/insurance price are always accurate.
   */
  function triggerSymmetryRefresh() {
    const cartForm = document.querySelector('cart-form[data-ajax-update]');
    if (cartForm && typeof cartForm.refresh === 'function') {
      cartForm.refresh();
    } else {
      // Fallback: fire the event Symmetry listens to
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
    }
  }

  // ─── API WRAPPERS ─────────────────────────────────────────────

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
    return res.json();
  }

  function markCartActivity() {
    lastCartActivityAt = Date.now();
  }

  function pause(ms) {
    return new Promise(resolve => window.setTimeout(resolve, ms));
  }

  function cartSignature(cart) {
    return (cart.items || []).map(item => [item.key, item.quantity, item.final_line_price]).join('|');
  }

  function bindEasyGiftEvents() {
    if (easyGiftEventsBound || !window.EasyGift || !window.EasyGift.Events ||
        typeof window.EasyGift.Events.subscribe !== 'function') return false;

    window.EasyGift.Events.subscribe('beforeCartRequest', () => {
      easyGiftRequestsInFlight += 1;
      markCartActivity();
    });
    window.EasyGift.Events.subscribe('afterCartRequest', () => {
      easyGiftRequestsInFlight = Math.max(0, easyGiftRequestsInFlight - 1);
      markCartActivity();
      scheduleInsuranceReconciliation();
    });
    easyGiftEventsBound = true;
    return true;
  }

  function scheduleEasyGiftEventBinding(attempt = 0) {
    if (bindEasyGiftEvents() || attempt >= 25) return;
    window.setTimeout(() => scheduleEasyGiftEventBinding(attempt + 1), 200);
  }

  function hasRecentEasyGiftActivity() {
    return easyGiftRequestsInFlight > 0 || (Date.now() - lastCartActivityAt) < 1100;
  }

  /**
   * EG owns product and free-gift mutations. Insurance is deliberately reconciled
   * only after EG has been quiet long enough to finish its serialized request queue.
   */
  function scheduleInsuranceReconciliation(delay = 1250) {
    window.clearTimeout(insuranceReconcileTimer);
    insuranceReconcileTimer = window.setTimeout(reconcileInsuranceAfterEasyGift, delay);
  }

  async function reconcileInsuranceAfterEasyGift() {
    if (insuranceReconcileRunning || isUpdating || hasRecentEasyGiftActivity()) {
      scheduleInsuranceReconciliation(350);
      return;
    }

    insuranceReconcileRunning = true;
    try {
      let cart = await fetchCart();
      if (hasRecentEasyGiftActivity()) {
        scheduleInsuranceReconciliation(350);
        return;
      }

      const { insuranceItem, hasRealItems } = analyzeCart(cart);
      const toggle = document.getElementById('lb-insurance-toggle');

      if (!hasRealItems) {
        insuranceOptedOut = false;
        if (toggle) toggle.checked = false;
        if (insuranceItem) {
          cart = await removeInsuranceFromCart(cart);
          triggerSymmetryRefresh();
        }
        updateAllDisplays(cart);
        return;
      }

      if (!toggle) {
        updateAllDisplays(cart);
        return;
      }

      if (!toggle.checked && !insuranceOptedOut && !insuranceItem) {
        toggle.checked = true;
      }

      if (!toggle.checked) {
        updateAllDisplays(cart);
        return;
      }

      const syncedCart = await syncInsuranceTier(cart);
      updateAllDisplays(syncedCart);
      if (cartSignature(cart) !== cartSignature(syncedCart)) triggerSymmetryRefresh();
    } catch (error) {
      console.error('[LadyBoss Cart] Deferred insurance reconciliation error:', error);
    } finally {
      insuranceReconcileRunning = false;
    }
  }

  /** Add variant — returns updated cart */
  async function addToCart(variantId, quantity = 1, properties = {}) {
    markCartActivity();
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties })
    });
    return fetchCart();
  }

  /** Update line item by key — returns updated cart */
  async function updateCartItem(key, quantity) {
    markCartActivity();
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    return res.json();
  }

  /** Remove all insurance line items — returns updated cart */
  async function removeInsuranceFromCart(cart) {
    const insuranceItems = cart.items.filter(i => i.handle === INSURANCE_PRODUCT_HANDLE);
    let updatedCart = cart;
    for (const item of insuranceItems) {
      updatedCart = await updateCartItem(item.key, 0);
    }
    return updatedCart;
  }

  // ─── DISPLAY UPDATES ─────────────────────────────────────────

  /**
   * Update subtotal, insurance price label, and shipping bar from a cart object.
   * Called immediately after any API change for instant visual feedback,
   * and again after Symmetry's merge completes (via on:cart:after-merge).
   */
  function updateAllDisplays(cart) {
    const { subtotalExInsurance, hasFreeShippingSub, hasRealItems } = analyzeCart(cart);

    // Subtotal
    const subtotalEl = document.getElementById('lb-cart-subtotal');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    // Insurance price label
    const displayEl = document.querySelector('[data-insurance-display]');
    if (displayEl) {
      const tier = getInsuranceTier(subtotalExInsurance);
      displayEl.textContent = formatMoney(tier.price);
    }

    // Shipping bar
    updateGiftMeterDOM(cart);
  }

  // GIFT_METER_PURE_START — pure display logic; no requests or cart writes.
  function getGiftMeterState(cart, productFlags = {}, promoTitle = '', claimsActive = false) {
    const format = cents => '$' + (Math.max(0, cents) / 100).toFixed(2).replace(/\.00$/, '');
    const definitions = [
      { kind: 'shipping', threshold: 7500, label: 'free U.S. shipping', variant: '' },
      { kind: 'shaker', threshold: 12500, label: 'your free purple shaker', variant: '48117746860261' },
      { kind: 'glow', threshold: 17500, label: 'your free GLOW', variant: '50430184554725' },
      { kind: 'tumbler', threshold: 25000, label: 'your free pink 40 oz tumbler', variant: '48121148670181' }
    ];
    const gifted = value => value !== undefined && value !== null && !['', 'false', '0'].includes(String(value).trim().toLowerCase());
    let spend = 0, shippingSpend = 0, known = true, paidItems = 0;
    const addedGifts = new Set();
    (cart.items || []).forEach(item => {
      const finalPrice = Math.max(0, Number(item.final_line_price) || 0);
      const isGift = gifted((item.properties || {})._Gifted);
      const variant = String(item.variant_id || item.id || '');
      if (isGift && finalPrice === 0) addedGifts.add(variant);
      if (item.handle === 'shipping-protection' || isGift || finalPrice === 0) return;
      let giftCard = item.gift_card;
      if (typeof giftCard !== 'boolean') giftCard = productFlags[variant];
      if (typeof giftCard !== 'boolean') known = false;
      if (giftCard === true) return;
      paidItems += 1;
      // Ajax line_price includes both line and order discounts. Do not use
      // final_line_price for shipping: Shopify excludes order discounts there.
      let shippingLine = Number(item.line_price);
      if (!Number.isFinite(shippingLine)) {
        if (Number.isFinite(Number(item.original_line_price)) && Array.isArray(item.discounts)) {
          shippingLine = Number(item.original_line_price) - item.discounts.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
        } else {
          known = false; // Native Liquid merge remains authoritative; no invented allocation.
          shippingLine = 0;
        }
      }
      shippingSpend += Math.max(0, shippingLine);
      // final_line_price already includes actual selling-plan pricing; do not use
      // compare-at/original retail or add back existing order-level BF tiers.
      let beforePromo = finalPrice;
      (item.line_level_discount_allocations || []).forEach(allocation => {
        if (!item.selling_plan_allocation && promoTitle && allocation.discount_application && String(allocation.discount_application.title).trim().toLowerCase() === promoTitle.trim().toLowerCase()) {
          beforePromo += Math.max(0, Number(allocation.amount) || 0);
        }
      });
      spend += beforePromo;
    });
    const currency = typeof cart.currency === 'string' ? cart.currency : cart.currency && cart.currency.iso_code;
    const precise = currency === 'USD' && known;
    const milestones = definitions.map(definition => {
      const basis = definition.kind === 'shipping' ? shippingSpend : spend;
      const reached = precise && basis >= definition.threshold;
      const added = !!(claimsActive && reached && definition.variant && addedGifts.has(definition.variant));
      return { ...definition, reached, added, remaining: Math.max(0, definition.threshold - basis),
        remainingText: !precise ? 'USD goal' : reached ? (added ? 'Added' : 'Goal reached') : format(definition.threshold - basis) + ' more',
        state: !precise ? 'Checkout eligibility' : reached ? (added ? 'Added to cart' : 'Goal reached') : 'Not yet reached' };
    });
    const next = milestones.find(milestone => !milestone.reached);
    let label = !precise ? 'Rewards eligibility is confirmed at checkout.'
      : next ? format(next.remaining) + ' more to ' + next.label
      : 'You reached every reward goal.';
    // A single fill cannot use the gift subtotal for shipping. The first
    // segment reflects net shipping spend; subsequent segments reflect gifts.
    let percent = 0;
    if (precise) {
      if (shippingSpend < 7500) percent = shippingSpend / 7500 * 30;
      else percent = Math.max(30, Math.min(100, spend / 25000 * 100));
    }
    return { spend, shippingSpend, precise, milestones, label,
      value: Math.round(percent * 100) / 100, max: 100, percent,
      empty: paidItems === 0, nextKind: precise && next ? next.kind : '' };
  }
  // GIFT_METER_PURE_END

  function updateGiftMeterDOM(cart) {
    document.querySelectorAll('.lb-cart-gift-meter').forEach(root => {
      let flags = {};
      try { flags = JSON.parse(root.querySelector('[data-meter-products]').textContent); } catch (_) { /* Native section merge supplies metadata. */ }
      const state = getGiftMeterState(cart, flags, root.dataset.promoTitle || '', root.dataset.claimsActive === 'true');
      root.hidden = state.empty;
      root.dataset.spend = String(state.spend);
      root.dataset.shippingSpend = String(state.shippingSpend);
      root.dataset.next = state.nextKind || 'complete';
      root.dataset.currency = state.precise ? 'USD' : '';
      const label = root.querySelector('[data-meter-label]');
      if (label && label.textContent !== state.label) label.textContent = state.label;
      const progress = root.querySelector('[data-meter-progress]');
      if (progress) {
        progress.hidden = !state.precise;
        progress.setAttribute('aria-valuenow', String(state.value));
        progress.setAttribute('aria-valuemax', String(state.max));
        progress.setAttribute('aria-valuetext', state.label);
      }
      const fill = root.querySelector('[data-meter-fill]');
      if (fill) fill.style.width = state.percent + '%';
      root.querySelectorAll('[data-meter-milestone]').forEach(node => {
        const milestone = state.milestones.find(item => item.kind === node.dataset.kind);
        if (!milestone) return;
        node.dataset.reached = String(milestone.reached);
        node.dataset.next = String(state.nextKind === milestone.kind);
        const remaining = node.querySelector('[data-meter-remaining]');
        const status = node.querySelector('[data-meter-state]');
        if (remaining && remaining.textContent !== milestone.remainingText) remaining.textContent = milestone.remainingText;
        if (status && status.textContent !== milestone.state) status.textContent = milestone.state;
      });
    });
  }

  // ─── INSURANCE LOGIC ─────────────────────────────────────────

  async function handleInsuranceToggle(checked) {
    if (isUpdating) return;
    insuranceOptedOut = !checked;
    isUpdating = true;

    const toggle       = document.getElementById('lb-insurance-toggle');
    const insuranceRow = document.querySelector('.lb-insurance');

    if (toggle) {
      toggle.disabled = true;
      toggle.setAttribute('aria-busy', 'true');
    }
    if (insuranceRow) insuranceRow.style.opacity = '0.55';
    announceInsuranceStatus('Updating Shipping Protection.');

    try {
      // Fetch cart once — used for both remove and add.
      let cart = await fetchCart();
      const { subtotalExInsurance } = analyzeCart(cart);
      const tier = getInsuranceTier(subtotalExInsurance);

      // Remove any existing insurance first.
      cart = await removeInsuranceFromCart(cart);

      if (checked && tier.variantId) {
        cart = await addToCart(tier.variantId, 1, { _insurance: 'true' });
      }

      // Instant display update
      updateAllDisplays(cart);
      announceInsuranceStatus(
        checked
          ? `Shipping Protection added. ${formatMoney(tier.price)} has been added to your order.`
          : 'Shipping Protection removed from your order.'
      );

      // Symmetry handles the DOM merge
      triggerSymmetryRefresh();

    } catch (err) {
      console.error('[LadyBoss Cart] Insurance toggle error:', err);
      if (toggle) toggle.checked = !checked;
      announceInsuranceStatus('Shipping Protection could not be updated. Please try again.');
    } finally {
      isUpdating = false;
      if (toggle) {
        toggle.disabled = false;
        toggle.removeAttribute('aria-busy');
      }
      if (insuranceRow) insuranceRow.style.opacity = '';
    }
  }

  /** Sync insurance to the correct tier after cart total changes */
  async function syncInsuranceTier(cart) {
    const toggle = document.getElementById('lb-insurance-toggle');
    if (!toggle || !toggle.checked) return cart;

    const { subtotalExInsurance, insuranceItem, hasRealItems } = analyzeCart(cart);
    const correctTier = getInsuranceTier(subtotalExInsurance);

    if (insuranceItem && String(insuranceItem.variant_id) === String(correctTier.variantId)) {
      return cart; // already correct
    }

    let updated = await removeInsuranceFromCart(cart);
    if (correctTier.variantId) {
      updated = await addToCart(correctTier.variantId, 1, { _insurance: 'true' });
    }
    return updated;
  }

  /**
   * Auto-add insurance on drawer open.
   * Only updates displays — does NOT trigger a section refresh
   * (the drawer was just opened, items are already rendered correctly).
   */
  async function autoAddInsuranceIfNeeded() {
    const toggle = document.getElementById('lb-insurance-toggle');
    if (!toggle || !toggle.checked) return;

    let cart = await fetchCart();
    const { subtotalExInsurance, insuranceItem, hasRealItems, hasFreeShippingSub } = analyzeCart(cart);

    if (!hasRealItems) return;

    if (insuranceItem) {
      // Already in cart — just update displays
      updateAllDisplays(cart);
      return;
    }

    // Add insurance
    const tier = getInsuranceTier(subtotalExInsurance);
    if (tier.variantId) {
      cart = await addToCart(tier.variantId, 1, { _insurance: 'true' });
      updateAllDisplays(cart);
      // Trigger a section refresh so the insurance line item appears in the list
      triggerSymmetryRefresh();
    }
  }

  // ─── QUANTITY UPDATES ─────────────────────────────────────────

  function handleQtyChange(key, newQty) {
    // Optimistic UI
    const itemRow = document.querySelector(`[data-merge-list-item*="item-key:${key}"]`);

    if (newQty <= 0 && itemRow) {
      itemRow.style.transition = 'opacity 0.18s, max-height 0.25s, padding 0.25s, margin 0.25s';
      itemRow.style.opacity    = '0';
      itemRow.style.maxHeight  = '0';
      itemRow.style.overflow   = 'hidden';
      itemRow.style.paddingTop    = '0';
      itemRow.style.paddingBottom = '0';
      itemRow.style.marginBottom  = '0';
      // If this item is a free-shipping-qualifying subscription, hide gifted items
      // ONLY if no other qualifying subscription remains in the cart
      if (itemRow.classList.contains('lb-cart-item') && itemRow.dataset.freeShippingSub === 'true') {
        const otherQualifyingSubs = document.querySelectorAll('.lb-cart-item[data-free-shipping-sub="true"]');
        const remainingCount = Array.from(otherQualifyingSubs).filter(row => row !== itemRow).length;
        if (remainingCount === 0) {
          hideGiftedItemsOptimistically();
        }
      }
    } else if (itemRow) {
      const qtyDisplay = itemRow.querySelector('.lb-qty__val');
      if (qtyDisplay) qtyDisplay.textContent = newQty;
      const minusBtn = itemRow.querySelector('.js-qty-minus');
      const plusBtn  = itemRow.querySelector('.js-qty-plus');
      if (minusBtn) minusBtn.dataset.qty = newQty - 1;
      if (plusBtn)  plusBtn.dataset.qty  = newQty + 1;
    }

    // Debounce API call 300ms
    if (qtyDebounce[key]) clearTimeout(qtyDebounce[key]);
    qtyDebounce[key] = setTimeout(() => {
      delete qtyDebounce[key];
      _executeQtyChange(key, newQty);
    }, 300);
  }

  // Hide free gift rows optimistically when a qualifying subscription item is removed.
  // EG Add to Cart will remove them server-side; this just makes it feel instant.
  function hideGiftedItemsOptimistically() {
    document.querySelectorAll('.lb-cart-item[data-gifted="true"]').forEach(row => {
      row.style.transition = 'opacity 0.2s, max-height 0.3s';
      row.style.opacity = '0';
      row.style.maxHeight = '0';
      row.style.overflow = 'hidden';
      row.style.marginBottom = '0';
      row.style.padding = '0';
    });
  }

  async function _executeQtyChange(key, newQty) {
    if (isUpdating) return;
    isUpdating = true;

    try {
      let cart = await updateCartItem(key, Math.max(0, newQty));
      const { hasRealItems } = analyzeCart(cart);

      if (!hasRealItems) {
        // Cart is empty — clear insurance and toggle in parallel with section refresh
        const toggle = document.getElementById('lb-insurance-toggle');
        if (toggle) toggle.checked = false;
        await Promise.all([
          removeInsuranceFromCart(cart),
          Promise.resolve(triggerSymmetryRefresh())
        ]);
        updateAllDisplays({ items: [], total_price: 0 });
      } else {
        // Run insurance sync and section refresh in parallel
        const [syncedCart] = await Promise.all([
          syncInsuranceTier(cart),
          Promise.resolve(triggerSymmetryRefresh())
        ]);
        updateAllDisplays(syncedCart || cart);
      }

    } catch (err) {
      console.error('[LadyBoss Cart] Qty update error:', err);
    } finally {
      isUpdating = false;
    }
  }

  // ─── EMPTY CART ───────────────────────────────────────────────

  async function handleEmptyCart() {
    if (isUpdating) return;
    isUpdating = true;
    try {
      // Optimistic: fade out all item rows immediately
      document.querySelectorAll('.lb-cart-item').forEach(row => {
        row.style.transition = 'opacity 0.2s';
        row.style.opacity = '0';
      });
      // Clear entire cart in one call
      await fetch('/cart/clear.js', { method: 'POST' });
      // The next real cart item should return to the required default-on state.
      insuranceOptedOut = false;
      const toggle = document.getElementById('lb-insurance-toggle');
      if (toggle) toggle.checked = false;
      // Update displays immediately
      updateAllDisplays({ items: [], total_price: 0 });
      // Let Symmetry re-render the empty state
      triggerSymmetryRefresh();
    } catch (err) {
      console.error('[LadyBoss Cart] Empty cart error:', err);
    } finally {
      isUpdating = false;
    }
  }

  // ─── UPSELL ADD TO CART ───────────────────────────────────────

  async function handleUpsellAdd(variantId, btn) {
    if (isUpdating) return;
    isUpdating = true;

    const originalText = btn.textContent;
    btn.classList.add('is-adding');
    btn.textContent = '✓';
    btn.disabled = true;

    // Hide the pill immediately
    const pill = btn.closest('.lb-upsell-pill');
    if (pill) {
      pill.style.transition = 'opacity 0.2s, max-height 0.25s';
      pill.style.opacity    = '0';
      pill.style.maxHeight  = '0';
      pill.style.overflow   = 'hidden';
      pill.style.marginBottom = '0';
    }

    try {
      let cart = await addToCart(variantId, 1);
      cart = await syncInsuranceTier(cart);
      updateAllDisplays(cart);
      triggerSymmetryRefresh();
    } catch (err) {
      console.error('[LadyBoss Cart] Upsell add error:', err);
      btn.classList.remove('is-adding', 'is-added');
      btn.textContent = originalText;
      btn.disabled = false;
      if (pill) {
        pill.style.opacity = '';
        pill.style.maxHeight = '';
      }
    } finally {
      isUpdating = false;
    }
  }

  // ─── EVENT DELEGATION ────────────────────────────────────────

  let delegationBound = false;

  function initCartListeners() {
    const drawer = document.querySelector('.lb-cart-drawer');
    if (!drawer || delegationBound) return;
    delegationBound = true;

    drawer.addEventListener('click', (e) => {
      const minusBtn = e.target.closest('.js-qty-minus');
      if (minusBtn) {
        e.preventDefault(); e.stopPropagation();
        handleQtyChange(minusBtn.dataset.key, parseInt(minusBtn.dataset.qty, 10));
        return;
      }
      const plusBtn = e.target.closest('.js-qty-plus');
      if (plusBtn) {
        e.preventDefault(); e.stopPropagation();
        handleQtyChange(plusBtn.dataset.key, parseInt(plusBtn.dataset.qty, 10));
        return;
      }
      const removeBtn = e.target.closest('.js-remove-item');
      if (removeBtn) {
        e.preventDefault(); e.stopPropagation();
        handleQtyChange(removeBtn.dataset.key, 0);
        return;
      }
      const upsellBtn = e.target.closest('.js-upsell-add');
      if (upsellBtn) {
        e.preventDefault(); e.stopPropagation();
        handleUpsellAdd(upsellBtn.dataset.variantId, upsellBtn);
        return;
      }
      const emptyBtn = e.target.closest('.js-empty-cart');
      if (emptyBtn) {
        e.preventDefault(); e.stopPropagation();
        handleEmptyCart();
        return;
      }
    });

    drawer.addEventListener('change', (e) => {
      if (e.target.id === 'lb-insurance-toggle') {
        handleInsuranceToggle(e.target.checked);
      }
    });

    // EG now owns checkout reconciliation through its native setting. Do not add
    // a second checkout poller here: extra cart reads were part of the conflict.

    // A merge may follow an EG mutation. Schedule one deferred reconciliation,
    // never an immediate cart fetch that competes with EG's serialized queue.
    drawer.addEventListener('on:cart:after-merge', () => {
      scheduleInsuranceReconciliation();
    });
  }

  // ─── INIT ─────────────────────────────────────────────────────

  function init() {
    initCartListeners();
    scheduleEasyGiftEventBinding();

    // Initial display update on page load
    fetchCart().then(cart => updateAllDisplays(cart));

    /**
     * Product forms and EG both emit cart activity. This handler deliberately makes
     * no cart request. It queues one insurance reconciliation after EG is idle.
     */
    document.addEventListener('on:cart:add', () => {
      markCartActivity();
      scheduleInsuranceReconciliation();
    });

    /**
     * Let a just-finished EG flow settle before reading cart state. When the drawer
     * opens independently, one normal display read is safe and keeps values current.
     */
    document.addEventListener('dispatch:cart-drawer:open', () => {
      if (hasRecentEasyGiftActivity()) {
        scheduleInsuranceReconciliation();
        return;
      }
      fetchCart().then(updateAllDisplays).catch(error => {
        console.error('[LadyBoss Cart] drawer open display error:', error);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();


